import { StyleSheet, Text, View } from "react-native";
import { Image } from "expo-image";
import { money } from "@libs/checkout-core";
import type { BuyerSession } from "@libs/checkout-core";
import { colors } from "./colors";

function dateLabel(value: string, locale = "en-GB") {
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return value;
  }
}

/** "1 × General admission · 17 May 2030, 20:00": what is being bought. */
export function orderDetailOf(session: BuyerSession, locale?: string) {
  const event = session.line_items[0]?.event;
  return [
    session.line_items
      .map((line) => `${line.quantity} × ${line.item_name}`)
      .join(", "),
    event ? dateLabel(event.starts_at, locale) : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/**
 * The order, read only: the web checkout's summary card (web/order-summary
 * .tsx) — the event, what is being bought and when, and the total. Tickets
 * are chosen before the checkout, never in it.
 */
export function OrderSummaryCard({
  session,
  locale,
}: {
  session: BuyerSession;
  locale?: string;
}) {
  const event = session.line_items[0]?.event;
  return (
    <OrderSummaryView
      title={event?.name ?? session.organizer.name}
      detail={orderDetailOf(session, locale)}
      fee={
        session.booking_fee_minor
          ? `Includes ${money(session.booking_fee_minor, session.currency, locale)} booking fee`
          : null
      }
      total={
        (session.total_minor ?? 0) > 0
          ? money(session.total_minor, session.currency, locale)
          : "Free"
      }
    />
  );
}

/**
 * The summary card itself, whoever's order it is: the engine's session
 * (OrderSummaryCard), a vendor's quoted offer, or the one item a purchase
 * sells (a music unlock's song, with its artwork), already in words.
 */
export function OrderSummaryView({
  title,
  detail,
  fee,
  total,
  approx,
  tax,
  duties,
  artwork,
}: {
  title: string;
  detail: string;
  /** "Includes £1.50 booking fee", when there is one. */
  fee: string | null;
  total: string;
  /** The total in the fan's own currency ("about £63"), under it, when the
   *  order is priced in another (a 1-tap listing's estimate). */
  approx?: string | null;
  /** "+ local tax", under the total, where the seller adds tax at checkout
   *  (LIS-1133). */
  tax?: string | null;
  /** "Import duties may be due on delivery", under the delivery, where the
   *  option chosen leaves them to the fan. */
  duties?: string | null;
  /** The item's artwork, at the card's start (blank when it has none);
   *  absent for a cart of tickets. */
  artwork?: string | null;
}) {
  return (
    <View style={styles.card} testID="checkout-order-summary">
      {artwork === undefined ? null : artwork ? (
        <Image
          accessibilityIgnoresInvertColors
          contentFit="cover"
          source={{ uri: artwork }}
          style={styles.artwork}
          testID="checkout-order-artwork"
        />
      ) : (
        <View style={styles.artwork} testID="checkout-order-artwork" />
      )}
      <View style={styles.body}>
        <Text numberOfLines={1} style={styles.title}>
          {title}
        </Text>
        <Text style={styles.detail}>{detail}</Text>
        {fee ? <Text style={styles.detail}>{fee}</Text> : null}
        {duties ? (
          <Text style={styles.detail} testID="checkout-order-duties">
            {duties}
          </Text>
        ) : null}
      </View>
      {approx || tax ? (
        <View style={styles.amounts}>
          <Text style={styles.total}>{total}</Text>
          {approx ? (
            <Text style={styles.detail} testID="checkout-order-approx">
              {approx}
            </Text>
          ) : null}
          {tax ? (
            <Text style={styles.detail} testID="checkout-order-tax">
              {tax}
            </Text>
          ) : null}
        </View>
      ) : (
        <Text style={styles.total}>{total}</Text>
      )}
    </View>
  );
}

/**
 * The card before the order is known: the same box and text lines, drawn
 * blank, so the card that takes its place has the height it had.
 */
export function OrderSummaryShape() {
  return (
    <View style={styles.card} testID="checkout-order-shape">
      <View style={styles.body}>
        <Text style={[styles.title, styles.blank]}>{"\u00a0"}</Text>
        <Text style={[styles.detail, styles.blank]}>{"\u00a0"}</Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    alignItems: "center",
    backgroundColor: colors.fill,
    borderRadius: 12,
    flexDirection: "row",
    gap: 12,
    justifyContent: "space-between",
    padding: 12,
  },
  artwork: {
    backgroundColor: colors.border,
    borderRadius: 8,
    height: 44,
    width: 44,
  },
  body: { flex: 1, gap: 2, minWidth: 0 },
  amounts: { alignItems: "flex-end", gap: 2 },
  title: { color: colors.text, fontSize: 15, fontWeight: "600" },
  detail: { color: colors.secondary, fontSize: 13 },
  blank: { color: "transparent" },
  total: {
    color: colors.text,
    fontSize: 15,
    fontVariant: ["tabular-nums"],
    fontWeight: "600",
  },
});
